import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle } from "lucide-react";

interface NotificationProps {
  message: string;
  type: "error" | "success";
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
}

export function Notification({
  message,
  type,
  isVisible,
  onClose,
  duration = 3000,
}: NotificationProps) {
  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 mx-3">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/30"
          />

          {/* Notification Card */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative bg-black/90 rounded-3xl px-8 py-6 max-w-sm w-full shadow-2xl"
          >
            {/* Icon */}
            <div className="flex justify-center mb-4">
              {type === "error" ? (
                <X className="w-12 h-12 text-white" strokeWidth={3} />
              ) : (
                <CheckCircle
                  className="w-12 h-12 text-white"
                  strokeWidth={2.5}
                />
              )}
            </div>

            {/* Message */}
            <p className="text-white text-center text-base leading-relaxed">
              {message}
            </p>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
